/**
 * Regroupement « Focus » de la vue Tâches : par urgence d'échéance.
 * Pur et sans dépendance au type Task complet (typage structurel) pour être
 * testable dans le harness vitest nu.
 */

export type FocusGroupKey = 'overdue' | 'today' | 'week' | 'later' | 'none' | 'done';

export interface FocusGroup<T> {
  key: FocusGroupKey;
  label: string;
  tasks: T[];
}

export type FocusTask = {
  due_date: string | null;
  due_datetime: string | null;
  parent_id: string | null;
  /** Optionnel : les terminées vont dans le groupe « Terminées », pas dans l'urgence. */
  is_completed?: boolean;
};

const LABELS: Record<FocusGroupKey, string> = {
  overdue: 'En retard',
  today: "Aujourd'hui",
  week: 'Cette semaine',
  later: 'Plus tard',
  none: 'Sans échéance',
  done: 'Terminées',
};

const ORDER: FocusGroupKey[] = ['overdue', 'today', 'week', 'later', 'none', 'done'];

function dueOf(t: FocusTask): string | null {
  return t.due_date ?? t.due_datetime ?? null;
}

/** Jours entre aujourd'hui (minuit local) et l'échéance (minuit local). */
function daysUntil(dueIso: string, now: Date): number | null {
  const due = new Date(dueIso);
  if (Number.isNaN(due.getTime())) return null;
  const msPerDay = 86_400_000;
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
  return Math.round((dueDay - startToday) / msPerDay);
}

/**
 * Groupes ordonnés En retard / Aujourd'hui / Cette semaine (dimanche inclus)
 * / Plus tard / Sans échéance / Terminées. Groupes vides omis, sous-tâches
 * exclues, tri interne par échéance croissante (« Sans échéance » et
 * « Terminées » gardent l'ordre reçu).
 */
export function groupTasksByUrgency<T extends FocusTask>(
  tasks: T[],
  now: Date = new Date(),
): FocusGroup<T>[] {
  // Dimanche = fin de semaine calendaire FR. getDay() : 0 = dimanche.
  const daysToSunday = (7 - now.getDay()) % 7;
  const buckets = new Map<FocusGroupKey, T[]>(ORDER.map((k) => [k, []]));

  for (const t of tasks) {
    if (t.parent_id) continue; // sous-tâches non gérées en V1 vues
    const due = dueOf(t);
    const d = due ? daysUntil(due, now) : null;
    let key: FocusGroupKey;
    if (t.is_completed) key = 'done';
    else if (d === null) key = 'none';
    else if (d < 0) key = 'overdue';
    else if (d === 0) key = 'today';
    else if (d <= daysToSunday) key = 'week';
    else key = 'later';
    buckets.get(key)!.push(t);
  }

  for (const key of ORDER) {
    if (key === 'none' || key === 'done') continue;
    buckets.get(key)!.sort((a, b) => (dueOf(a) ?? '').localeCompare(dueOf(b) ?? ''));
  }

  return ORDER.filter((k) => buckets.get(k)!.length > 0).map((key) => ({
    key,
    label: LABELS[key],
    tasks: buckets.get(key)!,
  }));
}

/**
 * Partition stable : les tâches terminées passent en fin, l'ordre relatif de
 * chaque moitié est conservé. Utilisé par les vues liste (sections) et kanban
 * quand « Afficher les terminées » est actif.
 */
export function completedLast<T extends { is_completed?: boolean }>(tasks: T[]): T[] {
  const active = tasks.filter((t) => !t.is_completed);
  const done = tasks.filter((t) => t.is_completed);
  return done.length === 0 ? tasks : [...active, ...done];
}
